/**
 * F-70 entry-point rule: WHEN does the translate icon appear in the F-27
 * selection toolbar? PURE logic (no React, no DOM, no Yjs) — the caller reads
 * the two board facts and hands them in.
 *
 * F-70's amended spec: "select any column that is *not* Original Script → a
 * translate icon appears in the F-27 selection toolbar … It translates the
 * entire Original Script column into the selected column." So the icon shows
 * for exactly ONE target column, which is never the F-28 source.
 *
 * The icon is HIDDEN (not disabled) when ineligible, unlike the toolbar's
 * add-comment button. That button is disabled-not-hidden because it is
 * *temporarily* unavailable (the comments feature has not bound its hook yet)
 * and the affordance flips live. Ineligibility here is a property of what the
 * user selected: a greyed translate icon on a 3-column selection would invite
 * the user to hunt for the setting that enables it, and there is none. Narrow
 * the selection and it appears — that is the discoverable path.
 *
 * Nothing here is a security boundary: `runTranslateColumn` re-resolves the
 * source from the doc (never from the request) and refuses a bad target with a
 * 400. This module's job is that the UI never OFFERS a translate the server
 * would reject.
 */
import type { ToolbarSelection } from "../../registries/toolbar-actions/index.js";

/** Why the translate icon is withheld (each maps to a real selection state). */
export type TranslateIneligibleReason =
  /** An element is selected — F-70 translates grid columns only. */
  | "not-cells"
  /**
   * The selection spans 0 or 2+ columns. F-70 translates ONE column per run
   * (one mapping, one dialog, one (from,to) pair), and picking which of a
   * multi-column selection is meant would be a guess.
   */
  | "multi-column"
  /**
   * No locked "Original Script" column on this sheet — a board seeded before
   * F-28. There is nothing to translate FROM, and the server says exactly this
   * (400 no-source-column) rather than guessing column 0.
   */
  | "no-source-column"
  /**
   * The selection IS the source column. Translating Original Script into
   * itself is the one thing F-28 exists to prevent.
   */
  | "is-source-column"
  /**
   * A locked column that is not the resolved source (only reachable on a doc
   * with two locked columns — the accessors never produce one). Withheld
   * because `setColumnTranslation` refuses ANY locked column, so the mapping
   * could not be persisted and "re-translate" would forget the languages.
   */
  | "locked-column"
  /**
   * The column is gone (C4: someone deleted it while the selection — or the
   * open dialog — still named it). Unreachable from a live grid selection;
   * the point is the re-check at submit time.
   */
  | "column-gone";

export type TranslateEligibility =
  | { ok: true; sheetId: string; colId: string; sourceColId: string }
  | { ok: false; reason: TranslateIneligibleReason };

/** The board facts the rule needs, as the caller reads them from `BoardDoc`. */
export interface TranslateColumnFacts {
  /** `board.sourceColumnId(sheetId)` — undefined on a pre-F-28 board. */
  sourceColId: string | undefined;
  /** `board.isColumnLocked(sheetId, colId)`. */
  isLocked(colId: string): boolean;
  /** `board.hasColumn(sheetId, colId)` — the C4 existence re-check. */
  hasColumn(colId: string): boolean;
}

/**
 * May THIS column be a translation target, and if not, why? The column half of
 * the rule, without a selection: it is what the open dialog re-checks at submit
 * time (C4 — it holds ids captured when it opened, not a live selection).
 *
 * Order matters: a deleted column reports "column-gone" rather than being
 * blamed on the source, since that is what the user actually needs to hear.
 */
export function translateColumnEligibility(
  sheetId: string,
  colId: string,
  facts: TranslateColumnFacts,
): TranslateEligibility {
  if (!facts.hasColumn(colId)) return { ok: false, reason: "column-gone" };
  const { sourceColId } = facts;
  if (sourceColId === undefined) return { ok: false, reason: "no-source-column" };
  if (colId === sourceColId) return { ok: false, reason: "is-source-column" };
  if (facts.isLocked(colId)) return { ok: false, reason: "locked-column" };
  return { ok: true, sheetId, colId, sourceColId };
}

/**
 * Is `selection` a legal F-70 translate target, and if not, why? The selection
 * half (kind + exactly one column) on top of the column half above — the
 * toolbar's question. Callers that only need the boolean use `canTranslate`.
 */
export function translateEligibility(
  selection: ToolbarSelection | null,
  facts: TranslateColumnFacts,
): TranslateEligibility {
  if (selection === null || selection.kind !== "cells") {
    return { ok: false, reason: "not-cells" };
  }
  if (selection.colIds.length !== 1) return { ok: false, reason: "multi-column" };
  const colId = selection.colIds[0];
  if (colId === undefined) return { ok: false, reason: "multi-column" };
  return translateColumnEligibility(selection.sheetId, colId, facts);
}

/** Show the translate icon for this selection? */
export function canTranslate(
  selection: ToolbarSelection | null,
  facts: TranslateColumnFacts,
): boolean {
  return translateEligibility(selection, facts).ok;
}

/**
 * Why a submit was refused, in the user's words. Only the reasons a dialog can
 * actually hit are phrased for a person: the dialog opens from an eligible
 * selection, so anything here means the board CHANGED underneath it (someone
 * deleted the column, or the source moved) — C4's "re-check ids at run time".
 */
export function ineligibleMessage(reason: TranslateIneligibleReason): string {
  switch (reason) {
    case "no-source-column":
      return 'This sheet has no "Original Script" column to translate from.';
    case "is-source-column":
      return '"Original Script" is the source column — it cannot be a translation target.';
    case "locked-column":
      return "That column is locked and cannot hold a translation.";
    case "column-gone":
      return "That column was deleted.";
    case "multi-column":
    case "not-cells":
      return "Select a single column to translate into.";
  }
}
